import { Size } from "chakra-react-select";
import { ReactElement, useCallback, useState } from "react";
import { FieldPath, FieldValues } from "react-hook-form";

import { ControlledInput, ControlledInputProperties } from "../ControlledInput/ControlledInput";
import { AsyncSearchSelect, AsyncSearchSelectProperties } from "./AsyncSearchSelect";

type ControlledSearchSelectProperties<T extends FieldValues, TName extends FieldPath<T>> = Omit<
  ControlledInputProperties<T, TName>,
  "onChange" | "render"
> &
  Partial<Pick<AsyncSearchSelectProperties<any>, "onChange">> &
  Pick<
    AsyncSearchSelectProperties<any>,
    | "creatableFunction"
    | "defaultOptions"
    | "getOptionLabel"
    | "getOptionValue"
    | "isClearable"
    | "isSearchable"
    | "loadOptions"
    | "renderOption"
    | "singleValue"
  >;

export const ControlledAsyncSearchSelect = <T extends FieldValues, TName extends FieldPath<T>>({
  creatableFunction,
  defaultOptions,
  getOptionLabel,
  getOptionValue,
  isClearable,
  isSearchable,
  loadOptions,
  onChange: componentOnChange,
  renderOption: RenderOption,
  singleValue,
  size,
  ...controlledProperties
}: ControlledSearchSelectProperties<T, TName>): ReactElement => {
  const [options, setOptions] = useState<readonly T[]>([]);

  const loadOptionsCached = useCallback(
    async (query: string) => {
      const options = await loadOptions(query);

      if (getOptionValue) {
        setOptions(options);
      }
      return options;
    },
    [getOptionValue, loadOptions, setOptions],
  );

  return (
    <ControlledInput
      {...controlledProperties}
      render={({ onChange, value, ...field }) => {
        return (
          <AsyncSearchSelect
            creatableFunction={creatableFunction}
            defaultOptions={defaultOptions}
            getOptionLabel={(opt) => getOptionLabel?.(opt) ?? ""}
            isClearable={isClearable}
            isSearchable={isSearchable}
            loadOptions={loadOptionsCached}
            renderOption={RenderOption ? (properties) => <RenderOption {...properties} /> : undefined}
            singleValue={singleValue}
            size={size as Size}
            {...field}
            onChange={(opt) => {
              if (opt && getOptionValue) {
                opt = getOptionValue(opt);
              }
              componentOnChange?.(opt);
              onChange(opt);
            }}
            value={getOptionValue ? (options.find((opt) => getOptionValue(opt) === value) ?? null) : value}
          />
        );
      }}
      size={size}
    />
  );
};
